import { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import moment from "moment";
import { useMemo } from "react";
import { ItemsTable } from "ui";

import {
  CONSENT_STATUS_COLORS,
  CONSENT_STATUS_LABELS,
  CONTACT_SOURCE_LABELS
} from "../../constants";
import { MarketingContact } from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./ContactsTable.module.scss";

interface ContactsTableProps {
  contacts: MarketingContact[];
  isLoading?: boolean;
  onRowClick: (contact: MarketingContact) => void;
}

const ContactsTable = ({
  contacts,
  isLoading,
  onRowClick
}: ContactsTableProps) => {
  const columns: Array<ColumnDef<MarketingContact, string>> = useMemo(
    () => [
      {
        id: "phoneNumber",
        accessorKey: "phoneNumber",
        header: t("Phone"),
        size: 18,
        minSize: 18,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.phone}>
            +{contacts[row.index].phoneNumber}
          </span>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: t("Name"),
        size: 20,
        minSize: 20,
        enableSorting: false,
        cell: ({ row }) =>
          contacts[row.index].name ?? (
            <span className={styles.muted}>{t("Unknown")}</span>
          )
      },
      {
        id: "source",
        accessorKey: "source",
        header: t("Source"),
        size: 13,
        minSize: 13,
        enableSorting: false,
        cell: ({ row }) => {
          const { source } = contacts[row.index];

          return source ? (
            <span>{t(CONTACT_SOURCE_LABELS[source])}</span>
          ) : (
            <span className={styles.muted}>—</span>
          );
        }
      },
      {
        id: "appInstalled",
        accessorKey: "appInstalled",
        header: t("App"),
        size: 9,
        minSize: 9,
        enableSorting: false,
        cell: ({ row }) =>
          contacts[row.index].appInstalled ? (
            <span className={styles.appInstalled}>{t("Installed")}</span>
          ) : (
            <span className={styles.muted}>—</span>
          )
      },
      {
        id: "lastOrderAt",
        accessorKey: "lastOrderAt",
        header: t("Last order"),
        size: 13,
        minSize: 13,
        enableSorting: false,
        cell: ({ row }) => {
          const { lastOrderAt } = contacts[row.index];

          return lastOrderAt ? (
            <span className={styles.nowrap}>
              {moment(lastOrderAt).format("DD/MM/YYYY")}
            </span>
          ) : (
            <span className={styles.muted}>—</span>
          );
        }
      },
      {
        id: "consent",
        accessorKey: "whatsappConsentStatus",
        header: t("Consent"),
        size: 14,
        minSize: 14,
        enableSorting: false,
        cell: ({ row }) => {
          const { whatsappConsentStatus } = contacts[row.index];

          return (
            <StatusBadge
              label={CONSENT_STATUS_LABELS[whatsappConsentStatus]}
              color={CONSENT_STATUS_COLORS[whatsappConsentStatus]}
            />
          );
        }
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("Added"),
        size: 13,
        minSize: 13,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.nowrap}>
            {moment(contacts[row.index].createdAt).format("DD/MM/YYYY")}
          </span>
        )
      }
    ],
    [contacts]
  );

  return (
    <ItemsTable
      columns={columns}
      data={contacts}
      isLoading={isLoading}
      skeletonHeight={64}
      emptyListMessage={t("No contacts yet")}
      rowsClassName={styles.tallRow}
      onRowClick={onRowClick}
    />
  );
};

export default ContactsTable;
